import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { DIRECTUS_INSTANCE } from '../../api';
import StandardTable from '../../ui/molecules/StandardTable';
import TableActions from '../../ui/molecules/TableActions';
import Pagination from '../../ui/molecules/Pagination';
import usePaginationWithURL from '../../hooks/usePaginationWithURL';
import { TrashIcon } from '@heroicons/react/24/outline';
import { formatDisplayDateTime } from '../../utils/dateFormatting';

const SkeletonRow = () => (
  <tr className="animate-pulse">
    <td className="py-4 pl-4 pr-3 sm:pl-6"><div className="h-4 bg-gray-200 rounded w-28" /></td>
    <td className="px-3 py-4"><div className="h-4 bg-gray-200 rounded w-16" /></td>
    <td className="px-3 py-4"><div className="h-4 bg-gray-200 rounded w-20" /></td>
    <td className="px-3 py-4"><div className="h-4 bg-gray-200 rounded w-44" /></td>
    <td className="px-3 py-4"><div className="h-4 bg-gray-200 rounded w-64" /></td>
    <td className="px-3 py-4"><div className="h-4 bg-gray-200 rounded w-32" /></td>
  </tr>
);

const EmptyState = ({ message }) => (
  <div className="px-4 py-8 text-center text-sm text-gray-500">{message}</div>
);

const buildLinkSignature = (link, area) => `${area}::${link?.url || ''}::${link?.text || ''}::${link?.section || ''}`;

const WebsiteSourcesPage = () => {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [cacheRecords, setCacheRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const {
    currentPage,
    itemsPerPage,
    totalPages,
    totalCount,
    setTotalCount,
    handlePageChange,
    handleItemsPerPageChange
  } = usePaginationWithURL({
    defaultItemsPerPage: 25,
    allowedItemsPerPage: [10, 25, 50, 100],
    scrollToTop: true,
    scrollOffset: 0
  });

  const fetchWebsiteSources = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('directus_token');
      const workspaceId = localStorage.getItem('workspace_id');
      if (!token || !workspaceId) {
        setRows([]);
        setCacheRecords([]);
        setSelectedItems(new Set());
        return;
      }

      const response = await axios.get(`${DIRECTUS_INSTANCE}/items/navigation_cache`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          fields: [
            'id',
            'domain',
            'navigation_data',
            'extracted_at',
            'date_updated',
            'total_links_count',
            'is_active'
          ],
          sort: ['-date_updated'],
          filter: JSON.stringify({
            _and: [{ linked_workspace: { _eq: Number(workspaceId) } }, { is_active: { _eq: true } }]
          }),
          limit: 100
        }
      });

      const records = response.data?.data || [];
      const flattenedRows = [];

      records.forEach((record) => {
        const headerLinks = record?.navigation_data?.header || [];
        const footerLinks = record?.navigation_data?.footer || [];

        headerLinks.forEach((link, index) => {
          if (!link?.url) return;
          flattenedRows.push({
            id: `${record.id}-header-${index}`,
            cacheId: record.id,
            domain: record.domain,
            area: 'header',
            section: link.section || '-',
            text: link.text || '-',
            url: link.url,
            extractedAt: record.extracted_at || record.date_updated || null,
            signature: buildLinkSignature(link, 'header')
          });
        });

        footerLinks.forEach((link, index) => {
          if (!link?.url) return;
          flattenedRows.push({
            id: `${record.id}-footer-${index}`,
            cacheId: record.id,
            domain: record.domain,
            area: 'footer',
            section: link.section || '-',
            text: link.text || '-',
            url: link.url,
            extractedAt: record.extracted_at || record.date_updated || null,
            signature: buildLinkSignature(link, 'footer')
          });
        });
      });

      setCacheRecords(records);
      setRows(flattenedRows);
      setSelectedItems(new Set());
    } catch (err) {
      setError(t('aiAgents.websiteSources.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchWebsiteSources();
  }, [fetchWebsiteSources]);

  useEffect(() => {
    setTotalCount(rows.length);
  }, [rows, setTotalCount]);

  useEffect(() => {
    setSelectedItems(new Set());
  }, [currentPage, itemsPerPage]);

  const handleSelectItem = (itemId) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.size === paginatedRows.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(paginatedRows.map((row) => row.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;
    const confirmDelete = window.confirm(
      t('aiAgents.websiteSources.confirmDelete', { count: selectedItems.size })
    );
    if (!confirmDelete) return;

    try {
      setDeleteLoading(true);
      setError('');

      const token = localStorage.getItem('directus_token');
      const selectedRows = rows.filter((row) => selectedItems.has(row.id));
      const groupedByCache = selectedRows.reduce((acc, row) => {
        if (!acc[row.cacheId]) acc[row.cacheId] = new Set();
        acc[row.cacheId].add(row.signature);
        return acc;
      }, {});

      const updatePromises = Object.entries(groupedByCache).map(async ([cacheId, signatures]) => {
        const record = cacheRecords.find((item) => String(item.id) === String(cacheId));
        if (!record) return;

        const currentHeader = record?.navigation_data?.header || [];
        const currentFooter = record?.navigation_data?.footer || [];

        const nextHeader = currentHeader.filter(
          (link) => !signatures.has(buildLinkSignature(link, 'header'))
        );
        const nextFooter = currentFooter.filter(
          (link) => !signatures.has(buildLinkSignature(link, 'footer'))
        );

        const navigationData = {
          ...(record.navigation_data || {}),
          header: nextHeader,
          footer: nextFooter
        };

        await axios.patch(
          `${DIRECTUS_INSTANCE}/items/navigation_cache/${cacheId}`,
          {
            navigation_data: navigationData,
            total_links_count: String(nextHeader.length + nextFooter.length)
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      });

      await Promise.all(updatePromises);
      await fetchWebsiteSources();
    } catch (err) {
      setError(t('aiAgents.websiteSources.errors.deleteFailed'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: 'domain',
        title: t('aiAgents.websiteSources.columns.domain'),
        render: (row) => <span className="text-sm text-gray-700">{row.domain || '-'}</span>,
        headerClassName: 'py-3.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider sm:pl-6',
        cellClassName: 'whitespace-nowrap py-3 pl-4 pr-3 text-sm text-gray-700 sm:pl-6'
      },
      {
        key: 'area',
        title: t('aiAgents.websiteSources.columns.area'),
        render: (row) => (
          <span className="text-sm text-gray-700">
            {row.area === 'header'
              ? t('aiAgents.websiteSources.areaHeader')
              : t('aiAgents.websiteSources.areaFooter')}
          </span>
        ),
        headerClassName: 'px-3 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider',
        cellClassName: 'whitespace-nowrap px-3 py-3 text-sm text-gray-700'
      },
      {
        key: 'section',
        title: t('aiAgents.websiteSources.columns.section'),
        render: (row) => <span className="text-sm text-gray-700">{row.section}</span>,
        headerClassName: 'px-3 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider',
        cellClassName: 'whitespace-nowrap px-3 py-3 text-sm text-gray-700'
      },
      {
        key: 'text',
        title: t('aiAgents.websiteSources.columns.label'),
        render: (row) => <span className="text-sm text-gray-900">{row.text}</span>,
        headerClassName: 'px-3 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider',
        cellClassName: 'px-3 py-3 text-sm text-gray-900'
      },
      {
        key: 'url',
        title: 'URL',
        render: (row) => (
          <a
            href={row.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:text-primary-700 break-all text-sm"
            onClick={(e) => e.stopPropagation()}
          >
            {row.url}
          </a>
        ),
        headerClassName: 'px-3 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider',
        cellClassName: 'px-3 py-3 text-sm'
      },
      {
        key: 'extractedAt',
        title: t('aiAgents.websiteSources.columns.extractedAt'),
        render: (row) => (
          <span className="text-sm text-gray-700">
            {formatDisplayDateTime(row.extractedAt, i18n.language)}
          </span>
        ),
        headerClassName: 'px-3 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider',
        cellClassName: 'whitespace-nowrap px-3 py-3 text-sm text-gray-700'
      }
    ],
    [i18n.language, t]
  );

  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return rows.slice(startIndex, startIndex + itemsPerPage);
  }, [rows, currentPage, itemsPerPage]);

  return (
    <div className="min-h-full bg-transparent">
      <div className="bg-white border-b border-gray-200 px-6 py-5">
        <h3 className="text-2xl font-medium text-gray-900 mb-2">{t('aiAgents.menu.websiteSources')}</h3>
        <p className="text-sm text-gray-600">
          {t('aiAgents.websiteSources.description')}
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && <div className="mb-5 p-4 bg-red-50 text-red-700 rounded-md">{error}</div>}

        <div className="mb-6 flex flex-col items-start gap-2">
          <TableActions
            actions={[
              {
                label: deleteLoading
                  ? t('aiAgents.websiteSources.deleting')
                  : `${t('aiAgents.websiteSources.deleteSelected')}${selectedItems.size ? ` (${selectedItems.size})` : ''}`,
                icon: TrashIcon,
                onClick: handleDeleteSelected,
                disabled: selectedItems.size === 0 || deleteLoading,
                loading: deleteLoading,
                variant: 'outline'
              }
            ]}
          />
          <div className="text-sm text-gray-600">
            {t('aiAgents.websiteSources.totalUrls', { count: totalCount })}
          </div>
        </div>

        <StandardTable
          columns={columns}
          data={paginatedRows}
          loading={loading}
          selectable
          selectedItems={selectedItems}
          onSelectItem={handleSelectItem}
          onSelectAll={handleSelectAll}
          SkeletonRow={SkeletonRow}
          EmptyState={EmptyState}
          emptyStateProps={{ message: t('aiAgents.websiteSources.empty') }}
          className="mt-0"
        />
        {!loading && rows.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            totalCount={totalCount}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
      </div>
    </div>
  );
};

export default WebsiteSourcesPage;
